import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, { type AriaRole, type CSSProperties, type ReactNode, useRef } from 'react'

import { useDeferredModalPresence } from '@src/quince-site-common/src/hooks/use-deferred-modal-presence'
import stopPropagationHandler from '@src/quince-site-common/src/utils/stopPropogationHandler'

import useLockBodyScroll from './hooks/useLockBodyScroll'
import Cross from './icons/cross.inline.svg'
import styles from './modal.module.scss'

import type { SystemStyleObject } from '@lastbrand/web-ui-styled-system/types'

export interface ModalProps {
  children: ReactNode
  open: boolean
  onClose?: () => void
  className?: string
  closeButtonClassName?: string
  modalWrapperClassName?: string
  closeOnBGClick?: boolean
  showCloseButton?: boolean
  crossAlignment?: 'left' | 'right'
  closeButtonContainerClassName?: string
  variant?: 'default' | 'bottomSheet'
  responsiveBottomSheet?: boolean
  role?: AriaRole
  'aria-labelledby'?: string
  'aria-label'?: string
  'aria-modal'?: boolean
  // DS-only: inline style on the web-ui dialog content (e.g. `{ padding: 0 }`); ignored by legacy.
  contentStyle?: CSSProperties
  // DS-only: Panda css on the web-ui <SheetContent> (bottom-sheet variant); ignored by legacy.
  sheetCss?: SystemStyleObject
}

const ModalLegacy = (props: ModalProps) => {
  const { t } = useTranslation('common')
  const {
    closeOnBGClick = true,
    open,
    crossAlignment = 'right',
    variant = 'default',
    responsiveBottomSheet = false,
  } = props
  const contentRef = useRef<HTMLDivElement>(null)

  // Body scroll intentionally keyed on the urgent `open` so it unlocks the instant a close begins.
  useLockBodyScroll(open, contentRef)

  // Instant open/close feedback: the shell follows `open` urgently while the heavy children
  // mount/unmount on the deferred pass. No Radix presence here, so a mounted-but-closed modal is
  // hidden with `display: none` (a style-only commit that paints immediately) until the deferred
  // teardown lands. No-op while the interaction-scheduling gate is off.
  const { mountOpen, contentOpen } = useDeferredModalPresence(open)

  if (!mountOpen) return null

  const isBottomSheet = variant === 'bottomSheet'

  return (
    <div
      style={open ? undefined : { display: 'none' }}
      onClick={closeOnBGClick ? props.onClose : undefined}
      className={classNames(
        styles.module_container,
        isBottomSheet && styles.module_container_bottomSheet,
        responsiveBottomSheet && styles.module_container_responsiveBottomSheet,
        props.modalWrapperClassName,
      )}
    >
      <div
        ref={contentRef}
        onClick={stopPropagationHandler}
        onTouchMove={stopPropagationHandler}
        role={props.role}
        aria-labelledby={props['aria-labelledby']}
        aria-label={props['aria-label']}
        aria-modal={props['aria-modal']}
        className={classNames(
          styles.modal,
          isBottomSheet && styles.modal_bottomSheet,
          responsiveBottomSheet && styles.modal_responsiveBottomSheet,
          props.className,
        )}
      >
        {props.showCloseButton && (
          <div className={props.closeButtonContainerClassName}>
            <Cross
              aria-label={t('close_button')}
              data-testid="modal-close-button"
              onClick={props.onClose}
              className={classNames(styles.cross_icon, props.closeButtonClassName, {
                [styles.cross_icon_left ?? '']: crossAlignment === 'left',
                [styles.cross_icon_right ?? '']: crossAlignment === 'right',
              })}
            />
          </div>
        )}
        {contentOpen ? props.children : null}
      </div>
    </div>
  )
}

export default ModalLegacy
